import { getI18n } from '@/plugins/i18n'
import { computed } from 'vue'

import * as dataGridColumn from '@/composables/data-grid-column'
import * as dataGrid from '@/composables/data-grid'
import { useRegistryStore } from '@/stores/registry.store'

import { codeNameDisplayExpr } from '@/composables/label'

export const useFormData = (selected, currentSupplierBrandRef, isBuy) => {
  const registryStore = useRegistryStore()
  const i18n = getI18n()

  return computed(() => [
    {
      itemType: 'group',
      caption: i18n.t('common.mainData'),
      colCount: 3,
      items: [
        {
          dataField: 'supplier_brand_ref',
          label: { text: i18n.t('common.supplierBrand') },
          disabled: !selected.value.includes('supplier_brand_ref'),
          editorType: 'dxSelectBox',
          editorOptions: {
            dataSource: {
              store: registryStore.supplierBrands,
              sort: 'code',
            },
            valueExpr: 'id',
            displayExpr: codeNameDisplayExpr,
            searchEnabled: true,
            showClearButton: true,
            onValueChanged: (e) => {
              currentSupplierBrandRef.value = e.value
            },
          },
          validationRules: selected.value.includes('supplier_brand_ref') ? [{ type: 'required' }] : []
        },
        {
          dataField: 'discount_category_ref',
          label: { text: i18n.t('common.discountCategory') },
          disabled: !selected.value.includes('discount_category_ref'),
          editorType: 'dxSelectBox',
          editorOptions: {
            dataSource: {
              store: currentSupplierBrandRef.value ? registryStore.getLocalizedDiscountCategories().filter(c => c.supplier_brand_ref === currentSupplierBrandRef.value) : registryStore.getLocalizedDiscountCategories(),
              paginate: true,
              pageSize: 25,
            },
            valueExpr: 'id',
            displayExpr: codeNameDisplayExpr,
            sort: 'name',
            showClearButton: true,
          },
          validationRules: selected.value.includes('discount_category_ref') ? [{ type: 'required' }] : []
        },
        {itemType: 'empty' },
        {
          dataField: 'supplier_ref',
          label: { text: i18n.t('common.supplier') },
          visible: isBuy.value,
          disabled: !selected.value.includes('supplier_ref'),
          editorType: 'dxSelectBox',
          editorOptions: {
            dataSource: {
              store: registryStore.suppliers,
              sort: 'code',
            },
            valueExpr: 'id',
            displayExpr: codeNameDisplayExpr,
            searchEnabled: true,
            showClearButton: true,
          },
          validationRules: selected.value.includes('supplier_ref') ? [{ type: 'required' }] : []
        },
        dataGridColumn.useCustomerColumn({
          label: { text: i18n.t('common.customer') },
          dataField: 'customer_id',
          template: 'customerEditor',
          editorType: 'dxSelectBox',
          visible: !isBuy.value,
          disabled: isBuy.value || !selected.value.includes('customer_id'),
          validationRules: selected.value.includes('customer_id') ? [{ type: 'required' }] : []
        }),
        {
          dataField: 'discount_list_ref',
          label: { text: i18n.t('common.discountGroup') },
          disabled: isBuy.value || !selected.value.includes('discount_list_ref'),
          editorType: 'dxSelectBox',
          editorOptions: {
            dataSource: {
              store: registryStore.getLocalizedDiscountLists(),
            },
            valueExpr: 'id',
            displayExpr: codeNameDisplayExpr,
            showClearButton: true,
          },
          validationRules: selected.value.includes('discount_list_ref') ? [{ type: 'required' }] : []
        },
        {itemType: 'empty'},
        {
          dataField: 'price_min',
          label: { text: i18n.t('common.priceMin') },
          disabled: isBuy.value || !selected.value.includes('priceRange'),
          editorType: 'dxNumberBox',
          editorOptions: {
            min: 0,
            value: selected.value.includes('priceRange') && !isBuy.value ? 0 : null,
            format: 'currency',
            showSpinButtons: true,
          },
          validationRules: selected.value.includes('price_min') ? [{ type: 'required' }] : []
        },
        {
          dataField: 'price_max',
          label: { text: i18n.t('common.priceMax') },
          disabled: isBuy.value || !selected.value.includes('priceRange'),
          editorType: 'dxNumberBox',
          editorOptions: {
            min: 0,
            value: selected.value.includes('priceRange') && !isBuy.value ? 0 : null,
            format: 'currency',
            showSpinButtons: true,
          },
          validationRules: selected.value.includes('price_max') ? [{ type: 'required' }] : []
        },
        {
          dataField: 'is_urgent',
          label: { text: i18n.t('common.urgency') },
          disabled: !selected.value.includes('is_urgent'),
          editorType: 'dxCheckBox',
        },
      ],
    },
    {
      itemType: 'group',
      caption: i18n.t('common.discountData'),
      colCount: 3,
      items: [
        {
          dataField: 'discount',
          label: { text: i18n.t('common.discount') },
          editorType: 'dxNumberBox',
          editorOptions: {
            format: { type: 'percent', precision: 2 },
            min: 0,
            value: 0,
            showSpinButtons: true,
            step: 0.01,
            ...dataGrid.useMaxDiscountValueOptions(),
          },
        },
        {
          dataField: 'discount_2',
          label: { text: i18n.t('common.discount') + ' 2' },
          editorType: 'dxNumberBox',
          editorOptions: {
            format: { type: 'percent', precision: 2 },
            min: 0,
            value: 0,
            showSpinButtons: true,
            step: 0.01,
            ...dataGrid.useMaxDiscountValueOptions(),
          },
        },
        {
          dataField: 'discount_3',
          label: { text: i18n.t('common.discount') + ' 3' },
          editorType: 'dxNumberBox',
          editorOptions: {
            format: { type: 'percent', precision: 2 },
            min: 0,
            value: 0,
            showSpinButtons: true,
            step: 0.01,
            ...dataGrid.useMaxDiscountValueOptions(),
          },
        },
        {
          dataField: 'max_applicable',
          label: { text: i18n.t('common.discountMaxApplicable') },
          editorType: 'dxNumberBox',
          editorOptions: {
            format: { type: 'percent', precision: 2 },
            min: 0,
            value: 0,
            showSpinButtons: true,
            step: 0.01,
            ...dataGrid.useMaxDiscountValueOptions(),
          },
        },
        { itemType: 'empty', colSpan: 2 },
        {
          dataField: 'charge_percentage',
          label: { text: i18n.t('common.chargePercentage') },
          editorType: 'dxNumberBox',
          editorOptions: {
            format: { type: 'percent', precision: 2 },
            min: 0,
            value: 0,
            showSpinButtons: true,
            step: 0.01,
          },
        },
      ],
    },
  ])
}

export const useFormStructure = (selected, isBuy) => {
  const i18n = getI18n()

  function updateSelected(key, value) {
    if (value) {
      if (!selected.value.includes(key)) selected.value.push(key)
    } else {
      selected.value = selected.value.filter(k => k !== key)
    }
    if (key === 'supplier_brand_ref') {
      selected.value = selected.value.filter(k => k !== 'discount_category_ref')
    }
    if (key === 'customer_id' || key === 'priceRange') {
      selected.value = selected.value.filter(k => k !== 'discount_list_ref')
    }
    if (key === 'discount_list_ref') {
      selected.value = selected.value.filter(k => k !== 'customer_id' && k !== 'priceRange')
    }
  }

  return computed(() => {
    const items = [
      {
        itemType: 'group',
        caption: i18n.t('common.mainData'),
        colCount: 3,
        cssClass: 'wizard-group',
        items: [
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.supplierBrand') },
            editorOptions: {
              value: selected.value.includes('supplier_brand_ref'),
              onValueChanged: e => updateSelected('supplier_brand_ref', e.value),
            },
          },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.discountCategory') },
            editorOptions: {
              value: selected.value.includes('discount_category_ref'),
              disabled: !selected.value.includes('supplier_brand_ref'),
              onValueChanged: e => updateSelected('discount_category_ref', e.value),
            },
          },
          { itemType: 'empty' },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.customer') },
            visible: !isBuy.value,
            editorOptions: {
              value: selected.value.includes('customer_id'),
              disabled: isBuy.value || selected.value.includes('discount_list_ref'),
              onValueChanged: e => updateSelected('customer_id', e.value),
            },
          },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.supplier') },
            visible: isBuy.value,
            editorOptions: {
              value: selected.value.includes('supplier_ref'),
              onValueChanged: e => updateSelected('supplier_ref', e.value),
            },
          },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.discountGroup') },
            editorOptions: {
              value: selected.value.includes('discount_list_ref'),
              disabled: isBuy.value || selected.value.includes('customer_id') || selected.value.includes('priceRange'),
              onValueChanged: e => updateSelected('discount_list_ref', e.value),
            },
          },
          { itemType: 'empty' },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.priceRange') },
            editorOptions: {
              value: selected.value.includes('priceRange'),
              disabled: isBuy.value || selected.value.includes('discount_list_ref'),
              onValueChanged: e => updateSelected('priceRange', e.value),
            },
          },
          {
            editorType: 'dxCheckBox',
            label: { text: i18n.t('common.urgency') },
            editorOptions: {
              value: selected.value.includes('is_urgent'),
              onValueChanged: e => updateSelected('is_urgent', e.value),
            },
          },
        ],
      },
    ]

    return items
  })
}

export const useColumns = (dataGrid) => {
  const registryStore = useRegistryStore()
  const i18n = getI18n()

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 0,
      fixed: false
    },
    {
      dataField: 'type',
      caption: i18n.t('common.discountType'),
      dataType: 'string',
      lookup: {
        dataSource: [
          { type: registryStore.subjectDiscountTypes.SELL, name: i18n.t('common.discountSell') },
          { type: registryStore.subjectDiscountTypes.BUY, name: i18n.t('common.discountBuy') },
        ],
        valueExpr: 'type',
        displayExpr: 'name',
      },
    },
    dataGridColumn.useSupplierBrandColumn({ validationRules: [] }),
    dataGridColumn.useDiscountCategoryColumn(),
    {
      caption: i18n.t('common.subject'),
      dataField: 'subject_ref',
      lookup: dataGrid.useLookup({ store: registryStore.getSubjects()}),
      allowEditing: false,
      formItem: { visible: false },
      allowSorting: true,
      allowFiltering: true,
    },
    dataGridColumn.useCustomerColumn({
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      visible: false,
      showInColumnChooser: false,
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useCustomerColumn().setCellValue(newData, value)
        newData.customer = value || { ...webSiteStore.emptyCustomer }
        newData.customer_id = newData.customer?.id
      },
      validationRules: []
    }),
    dataGridColumn.useSupplierColumn({ visible: false, validationRules: [], showInColumnChooser: false }),
    {
      dataField: 'discount_list_ref',
      caption: i18n.t('common.discountGroup'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedDiscountLists()}),
    },
    dataGridColumn.usePriceColumn({ dataField: 'price_min', caption: i18n.t('common.priceMin'), validationRules: [] }),
    dataGridColumn.usePriceColumn({ dataField: 'price_max', caption: i18n.t('common.priceMax'), validationRules: [] }),
    dataGridColumn.useDiscountColumn(),
    dataGridColumn.useDiscount2Column(),
    dataGridColumn.useDiscount3Column(),
    {
      dataField: 'charge_percentage',
      caption: i18n.t('common.chargePercentage'),
      dataType: 'number',
      ...dataGrid.usePercentageOptions(),
    },
    {
      dataField: 'max_applicable',
      caption: i18n.t('common.discountMaxApplicable'),
      dataType: 'number',
      ...dataGrid.usePercentageOptions(),
    },
    {
      dataField: 'is_urgent',
      caption: i18n.t('common.urgency'),
      dataType: 'boolean',
    },
  ]
}
